import { createTheme, type CSSObject } from '@mui/material/styles';

import { displayHeadingTrailingRuleSx } from 'src/common/styles/displayHeadingRuleStyles';
import { getGlobalScrollbarStyleOverrides } from 'src/common/styles/scrollbarStyles';

const displayFontFamily = ['Playfair Display', 'serif'].join(',');
const bodyFontFamily = ['Inter', 'sans-serif'].join(',');
const labelFontFamily = ['JetBrains Mono', 'monospace'].join(',');

const displayHeading = {
  fontFamily: displayFontFamily,
  fontStyle: 'italic' as const,
  fontWeight: 700,
};

const labelStyles = {
  fontFamily: labelFontFamily,
  textTransform: 'uppercase' as const,
  letterSpacing: '0.17em',
  fontWeight: 400,
  fontSize: '0.75rem',
};

const theme = createTheme({
  palette: {
    primary: {
      main: '#9C5A37',
      contrastText: '#EFEAD8',
    },
    secondary: {
      main: '#404D44',
      contrastText: '#EFEAD8',
    },
    error: {
      main: '#C84630',
    },
    warning: {
      main: '#E09F3E',
    },
    info: {
      main: '#B9E6FF',
    },
    success: {
      main: '#74C69D',
    },
    background: {
      default: '#EFEAD8',
      paper: '#F6F3EF',
    },
    text: {
      primary: '#1A2421',
      secondary: '#404D44',
    },
    divider: 'rgba(26, 36, 33, 0.12)',
  },
  spacing: 8,
  shape: {
    borderRadius: 6,
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: theme => ({
        ...getGlobalScrollbarStyleOverrides(theme),
        // MUI digital clock hides overflow until hover, then re-snaps scroll to the
        // selected value. Keep columns independently scrollable if that view is used.
        '.MuiMultiSectionDigitalClockSection-root': {
          overflowAnchor: 'none',
          overflowY: 'auto !important',
          overscrollBehavior: 'contain',
          scrollBehavior: 'auto !important',
        },
        // Display-heading h3 trailing-rule styles stretch the hour button to full
        // width, so the minutes sit on the far right. Keep time digits packed.
        '.MuiDateTimePickerToolbar-timeDigitsContainer': {
          gap: 0,
          '& .MuiButton-root': {
            flex: '0 0 auto',
            minWidth: 0,
          },
          '& .MuiTypography-h3': {
            alignSelf: 'auto',
            display: 'block',
            width: 'auto',
            '&::after': {
              content: 'none',
              display: 'none',
            },
          },
        },
        '.MuiDateTimePickerToolbar-separator': {
          marginLeft: 0,
          marginRight: 2,
        },
      }),
    },
    MuiButton: {
      defaultProps: {
        size: 'small',
      },
      styleOverrides: {
        root: {
          borderRadius: 6,
        },
        sizeSmall: {
          minHeight: 40,
        },
      },
    },
    MuiButtonGroup: {
      defaultProps: {
        size: 'small',
      },
      styleOverrides: {
        root: {
          borderRadius: 6,
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 6,
        },
      },
    },
    MuiToggleButtonGroup: {
      styleOverrides: {
        root: {
          borderRadius: 6,
        },
      },
    },
    MuiToggleButton: {
      styleOverrides: {
        root: {
          borderRadius: 6,
        },
      },
    },
    MuiList: {
      defaultProps: {
        dense: true,
      },
    },
    MuiMenuItem: {
      defaultProps: {
        dense: true,
      },
    },
    MuiTable: {
      defaultProps: {
        size: 'small',
      },
    },
    MuiTooltip: {
      defaultProps: {
        arrow: true,
      },
    },
    MuiCheckbox: {
      defaultProps: {
        size: 'small',
      },
    },
    MuiFab: {
      defaultProps: {
        size: 'small',
      },
    },
    MuiFormControl: {
      defaultProps: {
        margin: 'dense',
        size: 'small',
      },
    },
    MuiFormHelperText: {
      defaultProps: {
        margin: 'dense',
      },
    },
    MuiIconButton: {
      defaultProps: {
        size: 'small',
      },
    },
    MuiInputBase: {
      defaultProps: {
        margin: 'dense',
      },
    },
    MuiInputLabel: {
      defaultProps: {
        margin: 'dense',
      },
    },
    MuiRadio: {
      defaultProps: {
        size: 'small',
      },
    },
    MuiSwitch: {
      defaultProps: {
        size: 'small',
      },
    },
    MuiTextField: {
      defaultProps: {
        margin: 'dense',
        size: 'small',
        variant: 'outlined',
      },
    },
    MuiAppBar: {
      defaultProps: {
        color: 'transparent',
      },
    },
    MuiTypography: {
      styleOverrides: {
        h2: ({ ownerState }) =>
          ownerState.disableTrailingRule
            ? ({
                display: 'block',
                width: 'auto',
                alignSelf: 'auto',
                '&::after': { content: 'none', display: 'none' },
              } as CSSObject)
            : (displayHeadingTrailingRuleSx as CSSObject),
        h3: ({ ownerState }) =>
          ownerState.disableTrailingRule
            ? ({
                display: 'block',
                width: 'auto',
                alignSelf: 'auto',
                '&::after': { content: 'none', display: 'none' },
              } as CSSObject)
            : (displayHeadingTrailingRuleSx as CSSObject),
      },
    },
  },
  typography: {
    h1: {
      fontSize: '3rem',
      ...displayHeading,
    },
    h2: {
      fontSize: '2.5rem',
      ...displayHeading,
    },
    h3: {
      fontSize: '2.2rem',
      ...displayHeading,
    },
    h4: {
      fontSize: '2rem',
      ...displayHeading,
    },
    h5: {
      fontSize: '1.2rem',
      ...displayHeading,
    },
    h6: {
      fontSize: '1.1rem',
      ...displayHeading,
    },
    button: {
      fontSize: '0.7rem',
    },
    overline: labelStyles,
    label: labelStyles,
    fontSize: 14,
    fontWeightLight: 400,
    fontWeightRegular: 400,
    fontWeightMedium: 500,
    fontWeightBold: 700,
    fontFamily: bodyFontFamily,
  },
  zIndex: {},
});

export default theme;
